"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.LineChartProvider = void 0;
const react_1 = __importStar(require("react"));
const useDebounce_1 = require("@/hooks/useDebounce");
const getBounds_1 = require("@/utils/getBounds");
const sortByKey_1 = require("@/utils/sortByKey");
const types_1 = require("../types");
const LineChartContext_1 = require("./LineChartContext");
const reducer_1 = require("./reducer");
const LineChartProvider = ({ children, datasets, xDataKey, fixedDots, initialDynamicDots, onUserChange, dragging = true, }) => {
    const isFirstRender = (0, react_1.useRef)(true);
    // Calculate global bounds across ALL datasets
    const [lowerBound, upperBound] = (0, react_1.useMemo)(() => {
        const allValues = datasets.flatMap((dataset) => dataset.map((point) => point[xDataKey]));
        if (allValues.length === 0)
            return [0, 0];
        return [Math.min(...allValues), Math.max(...allValues)];
    }, [datasets, xDataKey]);
    // Calculate per-dataset bounds for validation
    const datasetBounds = (0, react_1.useMemo)(() => datasets.map((dataset) => {
        const [lower, upper] = (0, getBounds_1.getBounds)({ data: dataset, key: xDataKey });
        return { lowerBound: lower, upperBound: upper };
    }), [datasets, xDataKey]);
    // Initialize dots state from fixedDots or initialDynamicDots
    const initialDots = (0, react_1.useMemo)(() => {
        if (initialDynamicDots) {
            return initialDynamicDots.map((dot) => ({
                sliderValue: dot.x,
                inputValue: dot.x.toString(),
                inputIsInValid: false,
            }));
        }
        return fixedDots.map((dot) => ({
            sliderValue: dot.x,
            inputValue: dot.x.toString(),
            inputIsInValid: false,
        }));
    }, [fixedDots, initialDynamicDots]);
    const [state, dispatch] = (0, react_1.useReducer)(reducer_1.reducer, {
        dots: initialDots,
        datasetBounds,
        lowerBound,
        upperBound,
        datasets,
        xDataKey,
        fixedDots,
    });
    (0, react_1.useEffect)(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        dispatch({
            type: types_1.ActionType.UPDATE_STATE,
            value: { datasets, lowerBound, upperBound, datasetBounds, fixedDots, initialDynamicDots },
        });
    }, [datasets, fixedDots, lowerBound, upperBound, datasetBounds, initialDynamicDots]);
    const handleUserChange = (0, react_1.useCallback)((value, dotIndex) => {
        onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(value, dotIndex);
    }, [onUserChange]);
    const debouncedOnUserChange = (0, useDebounce_1.useDebounce)(handleUserChange, 300);
    const handleSliderChange = (0, react_1.useCallback)((value, dotIndex = 0) => {
        const newValue = Math.round(value / 100) * 100;
        dispatch({ type: types_1.ActionType.SLIDER, value: newValue, dotIndex });
        debouncedOnUserChange(newValue, dotIndex);
    }, [debouncedOnUserChange]);
    const handleInputChange = (0, react_1.useCallback)((value, dotIndex = 0) => {
        var _a, _b;
        dispatch({ type: types_1.ActionType.INPUT, value, dotIndex });
        // Use dataset-specific bounds for validation
        const bounds = datasetBounds[dotIndex];
        const lower = (_a = bounds === null || bounds === void 0 ? void 0 : bounds.lowerBound) !== null && _a !== void 0 ? _a : lowerBound;
        const upper = (_b = bounds === null || bounds === void 0 ? void 0 : bounds.upperBound) !== null && _b !== void 0 ? _b : upperBound;
        const num = Number(value);
        if (!Number.isNaN(num) && value !== '') {
            if (num >= lower && num <= upper) {
                dispatch({ type: types_1.ActionType.SLIDER, value: num, dotIndex });
                debouncedOnUserChange(num, dotIndex);
            }
            else {
                dispatch({ type: types_1.ActionType.SET_INPUT_INVALID, dotIndex });
            }
        }
    }, [debouncedOnUserChange, datasetBounds, lowerBound, upperBound]);
    const handleReset = (0, react_1.useCallback)((dotIndex = 0) => {
        dispatch({ type: types_1.ActionType.RESET, dotIndex });
        const fixedDot = fixedDots[dotIndex];
        if (fixedDot) {
            handleUserChange(fixedDot.x, dotIndex);
        }
    }, [fixedDots, handleUserChange]);
    // Merge all datasets into a single array for Recharts
    const mergedData = (0, react_1.useMemo)(() => {
        // Create a map of all unique x-values across all datasets
        const xValuesMap = new Map();
        state.datasets.forEach((dataset, datasetIndex) => {
            dataset.forEach((point) => {
                const xValue = point[state.xDataKey];
                if (!xValuesMap.has(xValue)) {
                    xValuesMap.set(xValue, { [state.xDataKey]: xValue });
                }
                // Transform all keys with dataset suffix in one operation
                const suffixedPoint = Object.fromEntries(Object.entries(point)
                    .filter(([key]) => key !== state.xDataKey)
                    .map(([key, value]) => [`${key}_dataset${datasetIndex}`, value]));
                Object.assign(xValuesMap.get(xValue), suffixedPoint);
            });
        });
        // Convert map to sorted array
        return Array.from(xValuesMap.values()).sort((0, sortByKey_1.sortByKey)(state.xDataKey));
    }, [state.datasets, state.xDataKey]);
    // Find the closest data point to the current value for each dataset
    const activeDataPoints = (0, react_1.useMemo)(() => state.datasets.map((dataset, index) => {
        const dotState = state.dots[index] || { sliderValue: 0 };
        const sliderVal = dotState.sliderValue;
        if (!dataset.length)
            return dataset[0];
        let closest = dataset[0];
        let minDistance = Math.abs(dataset[0][state.xDataKey] - sliderVal);
        for (let i = 1; i < dataset.length; i++) {
            const distance = Math.abs(dataset[i][state.xDataKey] - sliderVal);
            if (distance < minDistance) {
                minDistance = distance;
                closest = dataset[i];
            }
            if (distance === 0)
                break;
        }
        return closest;
    }), [state.datasets, state.dots, state.xDataKey]);
    // Static data - rarely changes
    const staticValue = (0, react_1.useMemo)(() => ({
        xDataKey: state.xDataKey,
        lowerBound: state.lowerBound,
        upperBound: state.upperBound,
        datasetBounds: state.datasetBounds,
        fixedDots: state.fixedDots,
        draggingEnabled: dragging && state.datasets.length === 1,
        datasets: mergedData,
        datasetCount: state.datasets.length,
    }), [state.datasets, state.xDataKey, state.lowerBound, state.upperBound, state.datasetBounds, state.fixedDots, dragging, mergedData]);
    // Dynamic state - changes frequently
    const dynamicValue = (0, react_1.useMemo)(() => ({
        dots: state.dots,
        handleSliderChange,
        handleInputChange,
        handleReset,
        activeDataPoints,
    }), [state.dots, handleSliderChange, handleInputChange, handleReset, activeDataPoints]);
    return (react_1.default.createElement(LineChartContext_1.LineChartStaticContext.Provider, { value: staticValue },
        react_1.default.createElement(LineChartContext_1.LineChartDynamicContext.Provider, { value: dynamicValue }, children)));
};
exports.LineChartProvider = LineChartProvider;
